/**
 * Analytics utility for working with Google Analytics
 * Provides clean interface for tracking page views and events
 */

/**
 * Track a page view with Google Analytics
 * @param {string} path - The path to track (e.g., "/dashboard")
 * @param {string} title - The page title
 */
export const trackPageView = (path, title) => {
  if (window.gtag) {
    // ✅ PROPER method for SPA page view tracking
    window.gtag('event', 'page_view', {
      page_title: title,
      page_location: window.location.origin + path,
      page_path: path
    });
  }
};

/**
 * Track an event with Google Analytics
 * @param {string} action - The action name (e.g., "click", "submit")
 * @param {string} category - The event category (e.g., "engagement", "ecommerce")
 * @param {string} label - Additional label for the event
 * @param {object} additionalParams - Any additional parameters to track
 */
export const trackEvent = (action, category, label, additionalParams = {}) => {
  if (window.gtag) {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      ...additionalParams
    });
  }
};

/**
 * Track user identification (only call after user consent)
 * @param {string} userId - User ID to associate with current session
 * @param {object} userProperties - Additional user properties
 */
export const identifyUser = (userId, userProperties = {}) => {
  if (window.gtag) {
    window.gtag('set', 'user_properties', {
      user_id: userId,
      ...userProperties
    });
  }
};

/**
 * Track subscription start (begin_checkout equivalent for subscriptions)
 * @param {string} planId - Plan being subscribed to
 * @param {number} value - Plan price
 * @param {string} currency - Currency (default: USD)
 */
export const trackSubscriptionStart = (planId, value, currency = 'USD') => {
  if (window.gtag) {
    window.gtag('event', 'begin_checkout', {
      currency: currency,
      value: value,
      items: [{
        item_id: planId,
        item_name: `${planId.charAt(0).toUpperCase() + planId.slice(1)} Plan`,
        item_category: 'subscription',
        price: value,
        quantity: 1
      }]
    });
  }
};

/**
 * Track successful subscription purchase
 * @param {string} transactionId - Stripe session or subscription ID
 * @param {string} planId - Plan that was purchased
 * @param {number} value - Plan price
 * @param {string} currency - Currency (default: USD)
 */
export const trackSubscriptionPurchase = (transactionId, planId, value, currency = 'USD') => {
  if (window.gtag) {
    window.gtag('event', 'purchase', {
      transaction_id: transactionId,
      value: value,
      currency: currency,
      items: [{
        item_id: planId,
        item_name: `${planId.charAt(0).toUpperCase() + planId.slice(1)} Plan`,
        item_category: 'subscription',
        price: value,
        quantity: 1
      }]
    });
  }
};

/**
 * Track file upload events
 * @param {string} fileType - Type of file uploaded (csv, xlsx, etc.)
 * @param {number} fileSize - Size of file in bytes
 * @param {number} rowCount - Number of products/rows in file
 */
export const trackFileUpload = (fileType, fileSize, rowCount) => {
  if (window.gtag) {
    window.gtag('event', 'file_upload', {
      event_category: 'engagement',
      file_type: fileType,
      file_size: fileSize,
      row_count: rowCount,
      value: rowCount // Use row count as value for measuring engagement
    });
  }
};

/**
 * Track mapping completion
 * @param {string} sessionId - Mapping session ID
 * @param {number} productCount - Number of products mapped
 * @param {string} taxonomyType - Target taxonomy type
 * @param {number} processingTime - Time taken to process (in seconds)
 */
export const trackMappingComplete = (sessionId, productCount, taxonomyType, processingTime) => {
  if (window.gtag) {
    window.gtag('event', 'mapping_complete', {
      event_category: 'conversion',
      event_value: productCount,
      session_id: sessionId,
      product_count: productCount,
      taxonomy_type: taxonomyType,
      processing_time: processingTime
    });
  }
};

/**
 * Track file download events
 * @param {string} downloadType - Type of download (mapped_csv, excel, etc.)
 * @param {number} itemCount - Number of items downloaded
 */
export const trackDownload = (downloadType, itemCount) => {
  if (window.gtag) {
    window.gtag('event', 'download', {
      event_category: 'engagement',
      download_type: downloadType,
      item_count: itemCount,
      value: itemCount
    });
  }
};

/**
 * Track plan changes
 * @param {string} fromPlan - Current plan
 * @param {string} toPlan - New plan
 * @param {boolean} isUpgrade - Whether this is an upgrade or downgrade
 */
export const trackPlanChange = (fromPlan, toPlan, isUpgrade) => {
  if (window.gtag) {
    window.gtag('event', 'plan_change', {
      event_category: 'subscription',
      from_plan: fromPlan,
      to_plan: toPlan,
      is_upgrade: isUpgrade,
      value: isUpgrade ? 1 : 0 // 1 for upgrade, 0 for downgrade
    });
  }
};

export default {
  trackPageView,
  trackEvent,
  identifyUser,
  trackSubscriptionStart,
  trackSubscriptionPurchase,
  trackFileUpload,
  trackMappingComplete,
  trackDownload,
  trackPlanChange
}; 